// Portal contract signing PDF viewer.
//
// Renders a PDF from a signed URL using react-pdf (pdfjs). Two modes:
//
//   * paginated = false  (default; HUB /portal/contracts modal)
//        Inline scrollable list of every page. Signing unlocks the
//        moment the last page enters the viewport (IntersectionObserver
//        + scroll-progress fallback).
//
//   * paginated = true  (Launch Portal contract-signing surface)
//        Single-page viewer with Prev / Next controls, "Page X of Y"
//        indicator, and page-fit-to-width (still zoom-respecting).
//        Signing unlocks the moment the user reaches the FINAL page
//        without requiring any internal PDF scrolling.
//
// Provides zoom, genuine browser fullscreen and download controls
// in both modes. Zoom + current page + fullscreen are all local
// component state; a page/zoom/fullscreen change NEVER re-fetches
// the underlying PDF (``file`` prop is memoised on ``fileUrl``).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  ChevronLeft, ChevronRight, Download, Loader2,
  Maximize2, Minimize2, ZoomIn, ZoomOut,
} from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Point pdfjs at the worker file we copy into ``public/`` at build
// time (see ``scripts/copy-pdf-worker.js``). Using the public path
// keeps the worker URL stable in production — the previous
// ``new URL(..., import.meta.url)`` trick emitted a fingerprinted
// ``/static/media/pdf.worker.min.<hash>.js`` URL which some
// production ingresses were rewriting to ``index.html``, resulting
// in pdfjs raising:
//   Cannot read properties of undefined (reading 'WorkerMessageHandler')
// because it was parsing the SPA shell instead of the worker JS.
const WORKER_URL = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = WORKER_URL;

async function _probeWorker() {
  try {
    const res = await fetch(WORKER_URL, { method: "GET" });
    if (!res.ok) {
      return { ok: false, reason: `Worker HTTP ${res.status} at ${WORKER_URL}` };
    }
    const text = await res.text();
    if (!/WorkerMessageHandler/.test(text)) {
      return {
        ok: false,
        reason:
          `PDF worker at ${WORKER_URL} did not return the pdfjs bundle ` +
          `(got ${text.length} bytes of non-worker content). Check that ` +
          `\`public/pdf.worker.min.js\` is deployed and not being ` +
          `rewritten by the ingress.`,
      };
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: `Worker fetch failed: ${e?.message || e}` };
  }
}

const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2.4;
const ZOOM_STEP = 0.2;

export default function ContractPdfViewer({
  fileUrl,
  fileHeaders,
  downloadUrl,
  fileName = "contract.pdf",
  onReachedLastPage,
  onViewerError,
  minHeight = 560,
  // NEW (Feb 2026, Launch Portal v2): when true, the viewer renders
  // one page at a time with Prev/Next controls instead of an
  // inline scroll list. The signing gate then unlocks on reaching
  // the final page — no scroll interaction required. Default false
  // keeps the HUB /portal/contracts flow byte-for-byte unchanged.
  paginated = false,
  // Paginated only: open on the final page once loaded (used after dual acceptance).
  startAtLastPage = false,
}) {
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadError, setLoadError] = useState("");
  const [workerReady, setWorkerReady] = useState(null); // null=probing, true|false
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fitWidth, setFitWidth] = useState(0);
  const containerRef = useRef(null);
  const lastPageRef = useRef(null);
  const scrollBoxRef = useRef(null);
  const reachedRef = useRef(false);

  // Probe the worker once per mount — if the file at WORKER_URL isn't
  // the real pdfjs worker we show a clear error and NEVER call
  // onReachedLastPage, so the parent's sign button stays disabled.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const probe = await _probeWorker();
      if (cancelled) return;
      if (!probe.ok) {
        setWorkerReady(false);
        setLoadError(probe.reason);
        onViewerError?.(probe.reason);
      } else {
        setWorkerReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the "reached last page" latch + page position whenever a
  // new PDF URL is handed in. Critical because after signing we swap
  // the fileUrl from personalised → signed PDF, but by then the form
  // is already unlocked so no need to re-fire.
  useEffect(() => {
    reachedRef.current = false;
    setNumPages(0);
    setCurrentPage(1);
    setLoadError("");
  }, [fileUrl]);

  const onLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
    if (paginated && startAtLastPage) setCurrentPage(n);
    // Single-page contracts should immediately unlock signing —
    // belt-and-braces so the callback fires even before the user
    // interacts with Prev/Next (paginated) or before the observer
    // debounces (scroll mode).
    if (n === 1 && !reachedRef.current) {
      reachedRef.current = true;
      onReachedLastPage?.();
    }
  }, [onReachedLastPage, paginated, startAtLastPage]);

  // ------------------------------------------------------------------
  // PAGINATED-mode signing gate: fire the callback the first time
  // ``currentPage`` reaches the final page. Independent from the
  // scroll-mode observer/scroll listeners below — they never run
  // when paginated is true.
  useEffect(() => {
    if (!paginated) return;
    if (!numPages) return;
    if (reachedRef.current) return;
    if (currentPage >= numPages) {
      reachedRef.current = true;
      onReachedLastPage?.();
    }
  }, [paginated, currentPage, numPages, onReachedLastPage]);

  // ------------------------------------------------------------------
  // SCROLL-mode signing gate — unchanged, only runs when
  // ``paginated`` is false (HUB behaviour). Left intact so the HUB
  // /portal/contracts viewer keeps its byte-for-byte original UX.
  useEffect(() => {
    if (paginated) return undefined;
    if (!numPages) return undefined;
    const target = lastPageRef.current;
    const root = scrollBoxRef.current;
    if (!target || !root) return undefined;
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !reachedRef.current) {
          reachedRef.current = true;
          onReachedLastPage?.();
        }
      }
    }, { root, threshold: [0.01, 0.2, 0.6, 1.0] });
    io.observe(target);
    return () => io.disconnect();
  }, [paginated, numPages, onReachedLastPage]);

  useEffect(() => {
    if (paginated) return undefined;
    if (!numPages) return undefined;
    const root = scrollBoxRef.current;
    if (!root) return undefined;
    const onScroll = () => {
      if (reachedRef.current) return;
      const consumed = root.scrollTop + root.clientHeight;
      const total = root.scrollHeight;
      if (total <= 0) return;
      const remaining = total - consumed;
      const progress = consumed / total;
      if (remaining <= 40 || progress >= 0.9) {
        reachedRef.current = true;
        onReachedLastPage?.();
      }
    };
    root.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => root.removeEventListener("scroll", onScroll);
  }, [paginated, numPages, onReachedLastPage]);

  // ------------------------------------------------------------------
  // Fit-to-width tracking (paginated only). Watches the scroll
  // container's inner width and hands it to <Page width=…> so a
  // pdf page fills the available viewer width at zoom 1.0 and
  // scales proportionally at other zooms. A ResizeObserver keeps
  // it accurate on window resizes AND fullscreen transitions
  // (fullscreen changes the container's client width instantly).
  useLayoutEffect(() => {
    if (!paginated) return undefined;
    const el = scrollBoxRef.current;
    if (!el) return undefined;
    const measure = () => {
      // Subtract horizontal padding (px-4 => 32px total). Keeps a
      // small safety margin so the page never over-runs and forces
      // a horizontal scrollbar at zoom = 1.
      const raw = el.clientWidth - 32;
      setFitWidth(raw > 0 ? raw : 0);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [paginated]);

  const options = useMemo(() => ({
    cMapUrl: "https://unpkg.com/pdfjs-dist@3.11.174/cmaps/",
    cMapPacked: true,
  }), []);

  // ``file`` prop shape for react-pdf. Memoised on ``fileUrl`` +
  // ``fileHeaders`` (which the parent stabilises with a module-scope
  // frozen empty object) so pdfjs never re-fetches on page change /
  // zoom change / checkbox tick / fullscreen toggle.
  const fileProp = useMemo(() => {
    if (!fileUrl) return null;
    return {
      url: fileUrl,
      httpHeaders: fileHeaders || undefined,
      withCredentials: !!fileHeaders,
    };
  }, [fileUrl, fileHeaders]);

  // ---------------------------- fullscreen
  function enterFullscreen() {
    const el = containerRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
  }
  function exitFullscreen() {
    if (document.exitFullscreen) document.exitFullscreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
  }
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  // ---------------------------- paginated helpers
  const goPrev = useCallback(() => setCurrentPage((p) => Math.max(1, p - 1)), []);
  const goNext = useCallback(
    () => setCurrentPage((p) => (numPages ? Math.min(numPages, p + 1) : p)),
    [numPages],
  );
  const atFirst = currentPage <= 1;
  const atLast = numPages > 0 && currentPage >= numPages;

  // When paginated, the rendered <Page width> = fitWidth × zoom so
  // (a) zoom 1.0 ALWAYS fills the viewer width and (b) zoom is
  // still a multiplier on top of that. Falls back to a sensible
  // default before the ResizeObserver first fires (e.g. inside
  // JSDOM in unit tests, where clientWidth is 0).
  const paginatedPageWidth = useMemo(() => {
    if (!paginated) return undefined;
    const base = fitWidth > 0 ? fitWidth : 640;
    return Math.max(200, Math.round(base * zoom));
  }, [paginated, fitWidth, zoom]);

  // In fullscreen we swap the container out of its inline flow and
  // pin it to the viewport with an inline style. Prevents the
  // "modal-shaped popup" failure mode (the flex parent chain used
  // to constrain the fullscreened element's height so it rendered
  // as a floating card inside the fullscreen viewport instead of
  // occupying it). ``w-screen`` / ``h-screen`` alone don't cover
  // Safari + Firefox reliably in `:fullscreen`, hence the inline
  // 100vw/100vh style plus a fixed anchor.
  const containerStyle = isFullscreen
    ? { width: "100vw", height: "100vh", position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 2147483647 }
    : undefined;

  return (
    <div
      ref={containerRef}
      className="flex flex-col h-full bg-stone-100"
      style={containerStyle}
      data-testid="contract-pdf-viewer"
      data-paginated={paginated ? "true" : "false"}
      data-fullscreen={isFullscreen ? "true" : "false"}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-white flex-wrap">
        <div className="text-xs text-stone-500 flex items-center gap-2 tabular-nums">
          {paginated ? (
            <>
              <button
                type="button"
                onClick={goPrev}
                disabled={atFirst || !numPages}
                className="p-1 rounded border border-stone-200 bg-white hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="pdf-prev-page-btn"
                title="Previous page"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span data-testid="pdf-page-indicator" className="min-w-[80px] text-center text-stone-700 font-medium">
                {numPages ? `Page ${currentPage} of ${numPages}` : "Loading…"}
              </span>
              <button
                type="button"
                onClick={goNext}
                disabled={atLast || !numPages}
                className="p-1 rounded border border-stone-200 bg-white hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="pdf-next-page-btn"
                title="Next page"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          ) : numPages ? (
            <span data-testid="pdf-page-count">{numPages} {numPages === 1 ? "page" : "pages"}</span>
          ) : (
            <span>Loading…</span>
          )}
          <span className="text-stone-300">·</span>
          <span data-testid="pdf-zoom-level">{Math.round(zoom * 100)}%</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))}
            disabled={zoom <= MIN_ZOOM}
            className="p-1.5 rounded border border-stone-200 bg-white hover:bg-stone-50 disabled:opacity-40"
            data-testid="pdf-zoom-out-btn"
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))}
            disabled={zoom >= MAX_ZOOM}
            className="p-1.5 rounded border border-stone-200 bg-white hover:bg-stone-50 disabled:opacity-40"
            data-testid="pdf-zoom-in-btn"
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          {isFullscreen ? (
            <button
              type="button"
              onClick={exitFullscreen}
              className="px-2.5 py-1.5 rounded border border-stone-950 bg-stone-950 text-white hover:bg-stone-800 inline-flex items-center gap-1 text-xs font-semibold"
              data-testid="pdf-exit-fullscreen-btn"
              title="Exit full screen (Esc)"
              aria-label="Exit full screen"
            >
              <Minimize2 className="h-4 w-4" />
              <span>Exit full screen</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={enterFullscreen}
              className="p-1.5 rounded border border-stone-200 bg-white hover:bg-stone-50"
              data-testid="pdf-fullscreen-btn"
              title="Full screen"
              aria-label="Full screen"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
          {downloadUrl && (
            <a
              href={downloadUrl}
              download={fileName}
              target="_blank"
              rel="noreferrer"
              className="p-1.5 rounded border border-stone-200 bg-white hover:bg-stone-50 inline-flex items-center gap-1 text-xs"
              data-testid="pdf-download-btn"
              title="Download PDF"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Download</span>
            </a>
          )}
        </div>
      </div>

      {/* Scroll / page container. In scroll mode this element is the
          IntersectionObserver root. In paginated mode it hosts a
          single centred <Page> and its clientWidth drives fit-to-
          width so the page fills the visible viewer. */}
      <div
        ref={scrollBoxRef}
        className={
          paginated
            ? "flex-1 min-h-0 overflow-auto p-4 flex items-start justify-center"
            : "flex-1 min-h-0 overflow-auto p-4 space-y-4"
        }
        style={{ minHeight }}
        data-testid="pdf-scroll-container"
      >
        {loadError ? (
          <div
            className="text-red-800 bg-red-50 border border-red-200 rounded p-3 text-sm"
            data-testid="pdf-viewer-error"
          >
            <div className="font-semibold mb-1">Couldn&apos;t load the contract PDF</div>
            <div className="text-xs">{loadError}</div>
            <div className="text-xs mt-2 text-red-700">
              Please refresh the page. If this keeps happening, contact
              Creative Mojo — signing is disabled until the viewer loads.
            </div>
          </div>
        ) : workerReady === null ? (
          <div className="flex items-center justify-center py-16 text-stone-500 text-sm gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Preparing PDF viewer…
          </div>
        ) : workerReady === false ? (
          <div className="text-red-800 bg-red-50 border border-red-200 rounded p-3 text-sm">
            PDF viewer failed to start.
          </div>
        ) : (
          <Document
            file={fileProp}
            options={options}
            loading={
              <div className="flex items-center justify-center py-16 text-stone-500 text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading contract…
              </div>
            }
            onLoadSuccess={onLoadSuccess}
            onLoadError={(e) => {
              const msg = e?.message || "PDF failed to load";
              setLoadError(msg);
              onViewerError?.(msg);
            }}
          >
            {paginated ? (
              numPages ? (
                <div
                  key={currentPage}
                  className="mx-auto bg-white shadow-sm rounded overflow-hidden w-fit"
                  data-testid={`pdf-page-${currentPage}`}
                  data-is-last-page={currentPage === numPages ? "true" : "false"}
                >
                  <Page
                    pageNumber={currentPage}
                    width={paginatedPageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
              ) : null
            ) : (
              Array.from({ length: numPages }, (_, i) => {
                const pageNumber = i + 1;
                const isLast = pageNumber === numPages;
                return (
                  <div
                    key={pageNumber}
                    ref={isLast ? lastPageRef : undefined}
                    className="mx-auto bg-white shadow-sm rounded overflow-hidden w-fit"
                    data-testid={`pdf-page-${pageNumber}`}
                    data-is-last-page={isLast ? "true" : "false"}
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={zoom}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </div>
                );
              })
            )}
          </Document>
        )}
      </div>
    </div>
  );
}
